import {useState, useEffect, useRef} from 'react'
import {Button, Form, Modal, Spinner, Badge, ListGroup} from 'react-bootstrap'
import Select from 'react-select'
import {useMutation, useQuery, useLazyQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {useDebounce} from '../../../../../_metronic/helpers'
import {CREATE_PDF_ENVELOPE} from '../../../gql/mutations/pdfEnvelopesMutations'
import {GET_ALL_PDF_DOCUMENT_TEMPLATES_ACTIVE, SEARCH_CONTACTS_FOR_SIGNER} from '../../../gql/queries/pdfEnvelopesApiQueries'
import {GET_ALL_USERS_FILTER, GET_ALL_USERS_BASIC} from '../../../gql/queries/usersQuery'
import {NodePdfDocumentTemplate, PdfSignerRole, SignerFormInput, SigningLinkResult} from '../../../types/pdfEnvelopesApi'
import {prependBackendUrl} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {parseId} from '../../../helpers'
import {useGetUser} from '../../../store/userStore'
import {PdfWithFieldOverlay} from './PdfWithFieldOverlay'

interface Props {
  isOpen: boolean
  onClose: () => void
  preSelectedTemplateId?: string
  sourceType?: string
  sourceId?: string
  preAgentId?: string
  refetch?: () => void
}

interface ContactResult {
  id: string
  fullName: string
  email?: string | null
  phone?: string | null
}

type AgentOption = {label: string; value: string}

const ROLE_COLORS = ['#2980b9', '#27ae60', '#e74c3c', '#f39c12', '#8e44ad', '#16a085']

const EMPTY_SIGNER = (role: string, index: number): SignerFormInput => ({
  role,
  recipientName: '',
  recipientEmail: '',
  recipientPhone: '',
  signingOrderIndex: index,
})

function parseJsonArray(raw: any): any[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) } catch { return [] }
  }
  return []
}

function buildAgentOption(node: any): AgentOption {
  return {
    label: `${node.firstName} ${node.lastName} - ${node.email}${node.npn ? ` (${node.npn})` : ''}`,
    value: node.id,
  }
}

export const ModalRequestEnvelope = ({
  isOpen,
  onClose,
  preSelectedTemplateId,
  sourceType,
  sourceId,
  preAgentId,
  refetch,
}: Props) => {
  const authUser = useGetUser((s) => s.user)

  const [selectedTemplateId, setSelectedTemplateId] = useState(preSelectedTemplateId ?? '')
  const [selectedTemplate, setSelectedTemplate] = useState<NodePdfDocumentTemplate | null>(null)
  const [signingOrder, setSigningOrder] = useState<'SEQUENTIAL' | 'PARALLEL'>('SEQUENTIAL')
  const [signers, setSigners] = useState<SignerFormInput[]>([])
  const [sendEmail, setSendEmail] = useState(true)
  const [sendSms, setSendSms] = useState(false)
  const [getSigningLink, setGetSigningLink] = useState(false)
  const [signingLinks, setSigningLinks] = useState<SigningLinkResult[]>([])
  const [done, setDone] = useState(false)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)

  // Agent selector state
  const [agentId, setAgentId] = useState<string | null>(preAgentId ?? null)
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null)
  const [agentInput, setAgentInput] = useState('')
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([])
  const agentDeb = useDebounce(agentInput, 500)

  const [searchAgents, {data: agentsData, loading: agentsLoading}] = useLazyQuery(
    GET_ALL_USERS_FILTER,
    {fetchPolicy: 'no-cache'}
  )
  const [fetchAgent, {data: agentPreData}] = useLazyQuery(GET_ALL_USERS_BASIC)
  const agentPreNode = agentPreData?.allUsers?.edges?.[0]?.node

  // Contact search state
  const [contactSearchText, setContactSearchText] = useState<Record<number, string>>({})
  const [openDropdownIdx, setOpenDropdownIdx] = useState<number | null>(null)
  const [contactResults, setContactResults] = useState<ContactResult[]>([])
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const {data: templatesData} = useQuery(GET_ALL_PDF_DOCUMENT_TEMPLATES_ACTIVE, {
    fetchPolicy: 'no-cache',
  })
  const templates: NodePdfDocumentTemplate[] =
    templatesData?.allPdfDocumentTemplates?.edges?.map((e: any) => e.node) ?? []

  const [createEnvelope, {loading}] = useMutation(CREATE_PDF_ENVELOPE)

  const [searchContacts] = useLazyQuery(SEARCH_CONTACTS_FOR_SIGNER, {
    onCompleted: (data) => {
      setContactResults(data?.allContacts?.edges?.map((e: any) => e.node) ?? [])
    },
  })

  // Agent: fetch pre-selected agent on open (logged-in user or preAgentId)
  useEffect(() => {
    const targetId = preAgentId ?? authUser?.user?.id
    if (!targetId) return
    setAgentId(targetId)
    fetchAgent({variables: {id: Number(parseId(targetId))}})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preAgentId, authUser?.user?.id])

  // Agent: trigger search to populate options after pre-selection
  useEffect(() => {
    if (!agentPreNode) return
    searchAgents({variables: {name: agentPreNode.firstName, requireNpn: true}})
  }, [searchAgents, agentPreNode])

  // Agent: auto-select pre-loaded agent in the react-select
  useEffect(() => {
    if (!agentPreNode) return
    setSelectedAgent(buildAgentOption(agentPreNode))
    setAgentId(agentPreNode.id)
  }, [agentPreNode])

  // Agent: update options from search results
  useEffect(() => {
    if (!agentsData) return
    setAgentOptions(agentsData.allUsers.edges.map(({node}: any) => buildAgentOption(node)))
  }, [agentsData])

  // Agent: debounced search when user types
  useEffect(() => {
    if (!agentDeb) return
    searchAgents({variables: {name: agentDeb, requireNpn: true}})
  }, [searchAgents, agentDeb])

  // PDF preview
  useEffect(() => {
    let objectUrl: string | null = null
    if (!selectedTemplate?.pdfFileUrl) {
      setPdfBlobUrl(null)
      return
    }
    setPdfLoading(true)
    const rawUrl = prependBackendUrl(selectedTemplate.pdfFileUrl)
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    fetch(rawUrl, {headers: token ? {Authorization: `Bearer ${token}`} : {}})
      .then((r) => r.blob())
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob)
        setPdfBlobUrl(objectUrl)
      })
      .catch(() => setPdfBlobUrl(null))
      .finally(() => setPdfLoading(false))
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.id])

  const applyTemplate = (tpl: NodePdfDocumentTemplate | null) => {
    setSelectedTemplate(tpl)
    setPdfBlobUrl(null)
    setContactSearchText({})
    setOpenDropdownIdx(null)
    setContactResults([])
    if (tpl) {
      const roles: PdfSignerRole[] = parseJsonArray(tpl.signerRoles) as PdfSignerRole[]
      setSigners(roles.map((r, i) => EMPTY_SIGNER(r.role, i)))
    } else {
      setSigners([])
    }
  }

  useEffect(() => {
    if (preSelectedTemplateId && templates.length > 0) {
      const tpl = templates.find((t) => t.id === preSelectedTemplateId) ?? null
      applyTemplate(tpl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length, preSelectedTemplateId])

  const handleTemplateChange = (id: string) => {
    setSelectedTemplateId(id)
    applyTemplate(templates.find((t) => t.id === id) ?? null)
  }

  const updateSigner = (i: number, key: keyof SignerFormInput, val: string | number) => {
    setSigners((p) => p.map((s, idx) => (idx === i ? {...s, [key]: val} : s)))
  }

  const handleContactSearch = (signerIdx: number, text: string) => {
    setContactSearchText((prev) => ({...prev, [signerIdx]: text}))
    setOpenDropdownIdx(signerIdx)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (text.trim().length < 2) {
      setContactResults([])
      return
    }
    searchTimerRef.current = setTimeout(() => {
      searchContacts({variables: {name: text, first: 8}})
    }, 350)
  }

  const selectContact = (signerIdx: number, contact: ContactResult) => {
    setSigners((prev) =>
      prev.map((s, idx) =>
        idx === signerIdx
          ? {
              ...s,
              recipientName: contact.fullName,
              recipientEmail: contact.email ?? '',
              recipientPhone: contact.phone ?? '',
              contactId: contact.id,
            }
          : s
      )
    )
    setContactSearchText((prev) => ({...prev, [signerIdx]: ''}))
    setOpenDropdownIdx(null)
    setContactResults([])
  }

  const handleSubmit = async () => {
    if (!selectedTemplateId) { toast.error('Select a template'); return }
    if (!agentId) { toast.error('Select an agent'); return }
    if (!sendEmail && !sendSms && !getSigningLink) { toast.error('Select at least one delivery channel'); return }
    if (signers.find((s) => !s.recipientName.trim())) { toast.error('Complete the name of all signers'); return }
    try {
      const {data} = await createEnvelope({
        variables: {
          templateId: parseId(selectedTemplateId),
          title: selectedTemplate?.name ?? 'Document',
          signingOrder,
          sourceType: sourceType ?? null,
          sourceId: sourceId ?? null,
          agentId: parseId(agentId),
          signers: signers.map((s) => ({
            ...s,
            contactId: s.contactId ? parseId(s.contactId) : undefined,
          })),
          sendEmail,
          sendSms,
          getSigningLink,
        },
      })
      if (data?.pdfEnvelopeCreate?.ok) {
        toast.success('Envelope sent successfully')
        setSigningLinks(data.pdfEnvelopeCreate.signingLinks ?? [])
        setDone(true)
        refetch?.()
      } else {
        toast.error(data?.pdfEnvelopeCreate?.errorMessage || 'Error sending')
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const signerRoles: PdfSignerRole[] = parseJsonArray(selectedTemplate?.signerRoles) as PdfSignerRole[]
  const roleLabel = (role: string) => signerRoles.find((r) => r.role === role)?.label ?? role
  const roleColor = (role: string) => {
    const idx = signerRoles.findIndex((r) => r.role === role)
    return ROLE_COLORS[idx >= 0 ? idx % ROLE_COLORS.length : 0]
  }

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size='xl'
      backdrop='static'
      keyboard={false}
      dialogClassName='modal-fullscreen-lg-down'
    >
      <Modal.Header closeButton>
        <Modal.Title>Send Document for Signature (PDF)</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{padding: 0, display: 'flex', height: 'calc(100vh - 160px)', overflow: 'hidden'}}>
        {done ? (
          <div className='p-4 w-100'>
            <div className='alert alert-success fw-bold'>Document sent successfully.</div>
            {getSigningLink && signingLinks.length > 0 && (
              <>
                <p className='fw-bold mb-2'>Signing links:</p>
                {signingLinks.map((sl) => (
                  <div key={sl.role} className='mb-3 p-3 border rounded'>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                      <span style={{
                        display: 'inline-block',
                        background: roleColor(sl.role),
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 4,
                      }}>
                        {roleLabel(sl.role)}
                      </span>
                    </div>
                    <a href={sl.link} target='_blank' rel='noreferrer' style={{wordBreak: 'break-all', fontSize: 13}}>
                      {sl.link}
                    </a>
                  </div>
                ))}
              </>
            )}
          </div>
        ) : (
          <>
            {/* Left — PDF preview with field overlays */}
            <div style={{flex: '1 1 60%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid #ddd'}}>
              {!selectedTemplate?.pdfFileUrl ? (
                <div className='d-flex flex-column align-items-center justify-content-center h-100 text-muted' style={{background: '#e9ecef'}}>
                  <i className='bi bi-file-earmark-pdf' style={{fontSize: 48, opacity: 0.3}} />
                  <p className='mt-2' style={{fontSize: 13}}>Select a template to preview the PDF</p>
                </div>
              ) : pdfLoading ? (
                <div className='d-flex align-items-center justify-content-center h-100' style={{background: '#e9ecef'}}>
                  <Spinner animation='border' className='me-2' />
                  <span className='text-muted'>Loading PDF...</span>
                </div>
              ) : pdfBlobUrl ? (
                <PdfWithFieldOverlay
                  pdfUrl={pdfBlobUrl}
                  fields={parseJsonArray(selectedTemplate?.fieldDefinitions) as any[]}
                  signerRoles={signerRoles}
                />
              ) : (
                <div className='d-flex align-items-center justify-content-center h-100 text-muted' style={{background: '#e9ecef'}}>
                  Could not load PDF
                </div>
              )}
            </div>

            {/* Right — form */}
            <div style={{width: 340, flexShrink: 0, overflowY: 'auto', padding: '20px 16px', background: '#fff'}}>

              {/* Agent selector */}
              <Form.Group className='mb-4'>
                <Form.Label className='fw-bold' style={{fontSize: 13}}>Agent *</Form.Label>
                <Select
                  value={selectedAgent}
                  isLoading={agentsLoading}
                  placeholder='Search agent by name…'
                  options={agentOptions}
                  inputValue={agentInput}
                  onInputChange={setAgentInput}
                  onChange={(opt) => {
                    setSelectedAgent(opt as AgentOption | null)
                    setAgentId((opt as AgentOption | null)?.value ?? null)
                  }}
                  isClearable
                  styles={{
                    control: (base) => ({...base, fontSize: 13, minHeight: 32}),
                    option: (base) => ({...base, fontSize: 13}),
                  }}
                />
              </Form.Group>

              <Form.Group className='mb-4'>
                <Form.Label className='fw-bold' style={{fontSize: 13}}>PDF Template *</Form.Label>
                <Form.Select
                  size='sm'
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                >
                  <option value=''>-- Select --</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {signers.length > 1 && (
                <Form.Group className='mb-4'>
                  <Form.Label className='fw-bold' style={{fontSize: 13}}>Signing order</Form.Label>
                  <div className='d-flex flex-column gap-1'>
                    {(['SEQUENTIAL', 'PARALLEL'] as const).map((o) => (
                      <Form.Check
                        key={o}
                        type='radio'
                        label={o === 'SEQUENTIAL' ? 'Sequential (one at a time)' : 'Parallel (all at once)'}
                        checked={signingOrder === o}
                        onChange={() => setSigningOrder(o)}
                        style={{fontSize: 13}}
                      />
                    ))}
                  </div>
                </Form.Group>
              )}

              {signers.length > 0 && (
                <div className='mb-4'>
                  <p className='fw-bold mb-2' style={{fontSize: 13}}>Signers</p>
                  {signers.map((s, i) => (
                    <div key={i} className='mb-3'>
                      <div className='d-flex align-items-center gap-2 mb-1'>
                        <div style={{
                          width: 12, height: 12, borderRadius: 3, flexShrink: 0,
                          background: roleColor(s.role),
                        }} />
                        <span className='fw-semibold' style={{fontSize: 13, color: roleColor(s.role)}}>
                          {roleLabel(s.role)}
                        </span>
                        {signingOrder === 'SEQUENTIAL' && (
                          <span className='text-muted ms-auto' style={{fontSize: 11}}>Sign #{i + 1}</span>
                        )}
                      </div>

                      {/* Contact search */}
                      <div style={{position: 'relative', marginBottom: 4}}>
                        <Form.Control
                          size='sm'
                          placeholder='Search contact by name…'
                          value={contactSearchText[i] ?? ''}
                          onChange={(e) => handleContactSearch(i, e.target.value)}
                          onBlur={() => setTimeout(() => setOpenDropdownIdx(null), 200)}
                          style={{fontSize: 12}}
                        />
                        {openDropdownIdx === i && contactResults.length > 0 && (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
                            background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 180, overflowY: 'auto',
                          }}>
                            <ListGroup variant='flush'>
                              {contactResults.map((c) => (
                                <ListGroup.Item
                                  key={c.id}
                                  action
                                  onMouseDown={() => selectContact(i, c)}
                                  style={{fontSize: 12, padding: '5px 10px', cursor: 'pointer'}}
                                >
                                  <div className='fw-semibold'>{c.fullName}</div>
                                  {(c.email || c.phone) && (
                                    <div className='text-muted' style={{fontSize: 11}}>
                                      {[c.email, c.phone].filter(Boolean).join(' · ')}
                                    </div>
                                  )}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          </div>
                        )}
                      </div>

                      <Form.Control
                        size='sm'
                        placeholder='Full name *'
                        value={s.recipientName}
                        onChange={(e) => updateSigner(i, 'recipientName', e.target.value)}
                        className='mb-1'
                      />
                      <Form.Control
                        size='sm'
                        placeholder='Email'
                        type='email'
                        value={s.recipientEmail}
                        onChange={(e) => updateSigner(i, 'recipientEmail', e.target.value)}
                        className='mb-1'
                      />
                      <Form.Control
                        size='sm'
                        placeholder='Phone'
                        value={s.recipientPhone}
                        onChange={(e) => updateSigner(i, 'recipientPhone', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className='mb-2'>
                <p className='fw-bold mb-2' style={{fontSize: 13}}>Delivery channels</p>
                <Form.Check
                  type='checkbox'
                  label='Email'
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className='mb-1'
                  style={{fontSize: 13}}
                />
                <Form.Check
                  type='checkbox'
                  label='SMS'
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  className='mb-1'
                  style={{fontSize: 13}}
                />
                <Form.Check
                  type='checkbox'
                  label='Get signing link'
                  checked={getSigningLink}
                  onChange={(e) => setGetSigningLink(e.target.checked)}
                  style={{fontSize: 13}}
                />
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          {done ? 'Close' : 'Cancel'}
        </Button>
        {!done && (
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner size='sm' animation='border' /> : 'Send Document'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
